import type { TTaskStatus } from '@homepal/api-contract'
import React, { useState } from 'react'

import { ETaskStatus } from 'types/GlobalTask'
import { SxProps } from '@mui/material'

import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'
import { useTaskActions } from 'redux/actions/Tasks'
import { useTasks } from 'redux/hooks/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Text from 'components_new/atoms/Text'

import ChipTabs from 'components_new/molecules/ChipTabs'
import ContentBox from 'components_new/molecules/ContentBox'
import KanbanBoard from 'components_new/molecules/KanbanBoard'
import { Option } from 'components_new/molecules/SelectSearch'
import StandardFilter from 'components_new/molecules/StandardFilter'
import ToggleButton from 'components_new/molecules/ToggleButton'
import ValueText from 'components_new/molecules/ValueText'

import StandardSearch from 'components_new/organisms/StandardSearch'
import TaskItem from 'components_new/organisms/tasks/TaskItem'
import TasksTable from 'components_new/organisms/tasks/TasksTable'

import { formatUsername } from 'utils/functions'
import { useQuery } from 'utils/hooks'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

interface TasksKanbanBoardProps {
  locked?: {
    groups?: string[]
    users?: string[]
  }
  sx?: SxProps
}

const TasksKanbanBoard = (props: TasksKanbanBoardProps) => {
  const { locked, sx } = props

  const { t, translateEnum } = useTranslation()
  const { account } = useAccountSettings()
  const { accounts } = useAccounts()
  const { tasks } = useTasks()

  const [view, setView] = useState<'list' | 'kanban'>('kanban')

  // Filter state
  const [assignmentFilter, setAssignmentFilter] = useState<
    'all' | 'assigned-to-me'
  >('all')
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [selectedRealEstates, setSelectedRealEstates] = useState<string[]>([])
  const { tryUpdateTask } = useTaskActions()

  const { query, setQuery } = useQuery()
  const search = query['search'] || null

  const getFooter = (value: number) => (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between'
      }}
    >
      <Text color="text.secondary">{t('global', 'total')}</Text>
      <ValueText color="text.secondary" unit={'kr/år'} value={value} />
    </Box>
  )

  const userOptions: Option[] = accounts.map((account) => ({
    avatar: 'user' as const,
    body: account.email,
    id: account.id,
    title: formatUsername(account)
  }))

  // Only real estates that have tasks, so every option narrows to something.
  const realEstateOptions: Option[] = Array.from(
    new Set(
      tasks
        .map((task) => task.metric_filter_value)
        .filter((name): name is string => !!name)
    )
  )
    .sort(sortAlphabeticalAsc)
    .map((name) => ({ id: name, title: name }))

  const { myGroups, otherGroups } = useGroups()
  const groupOptions: Option[] = [...myGroups, ...otherGroups].map((group) => ({
    avatar: 'group' as const,
    color: group.profile_color,
    id: group.id,
    title: group.name
  }))

  // Use locked filters if provided, otherwise use selected filters
  const activeGroups =
    locked?.groups !== undefined ? locked.groups : selectedGroups
  const activeUsers = locked?.users !== undefined ? locked.users : selectedUsers

  const myTasks = tasks.filter(
    (item) =>
      item.responsible_account_id && item.responsible_account_id === account?.id
  )

  // Filter items based on active filters
  const filteredItems = tasks.filter((item) => {
    // Filter by search
    if (search) {
      if (
        !(item.title || t('global', 'unnamed'))
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())
      ) {
        return false
      }
    }

    // Filter by assignment (all vs assigned-to-me)
    if (assignmentFilter === 'assigned-to-me') {
      if (
        !item.responsible_account_id ||
        item.responsible_account_id !== account?.id
      ) {
        return false
      }
    }

    // Filter by real estate
    if (selectedRealEstates.length > 0) {
      if (
        !item.metric_filter_value ||
        !selectedRealEstates.includes(item.metric_filter_value)
      ) {
        return false
      }
    }

    // Filter by group
    if (activeGroups.length > 0) {
      if (
        !item.group_ids ||
        !activeGroups.some((ag) => item.group_ids?.some((id) => id === ag))
      ) {
        return false
      }
    }

    // Filter by user
    if (activeUsers.length > 0) {
      if (
        !item.responsible_account_id ||
        !activeUsers.some((au) => au === item.responsible_account_id)
      ) {
        return false
      }
    }

    return true
  })

  // Footer totals follow the column a card is placed in.
  const getNoiChangeSum = (status: ETaskStatus) =>
    filteredItems
      .filter((task) => task.status === status)
      .reduce((total, task) => total + (task.noi_change ?? 0), 0)

  const TASKS_KANBAN_COLUMNS = [
    {
      footer: getFooter(getNoiChangeSum(ETaskStatus.TODO)),
      icon: {
        color: translateEnum('task_status', ETaskStatus.TODO).color,
        name: translateEnum('task_status', ETaskStatus.TODO).iconName
      },
      id: ETaskStatus.TODO,
      index: 0,
      title: translateEnum('task_status', 'TODO').title
    },
    {
      footer: getFooter(getNoiChangeSum(ETaskStatus.IN_PROGRESS)),
      icon: {
        color: translateEnum('task_status', ETaskStatus.IN_PROGRESS).color,
        name: translateEnum('task_status', ETaskStatus.IN_PROGRESS).iconName
      },
      id: ETaskStatus.IN_PROGRESS,
      index: 1,
      title: translateEnum('task_status', ETaskStatus.IN_PROGRESS).title
    },
    {
      footer: getFooter(getNoiChangeSum(ETaskStatus.DONE)),
      icon: {
        color: translateEnum('task_status', ETaskStatus.DONE).color,
        name: translateEnum('task_status', ETaskStatus.DONE).iconName
      },
      id: ETaskStatus.DONE,
      index: 2,
      title: translateEnum('task_status', ETaskStatus.DONE).title
    }
  ]

  const handleTaskMove = (itemId: string, newStatus: string) => {
    tryUpdateTask(itemId, { status: newStatus as TTaskStatus })
  }

  // $TODO: loaders HP-14313

  return (
    <>
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1 }}>
          <ChipTabs
            disableAll={true}
            options={[
              {
                count: tasks.length,
                label: t('global', 'all'),
                value: 'all'
              },
              {
                count: myTasks.length,
                label: `${t('global', 'my')} ${t('tasks', 'tasks', true)}`,
                value: 'assigned-to-me'
              }
            ]}
            onChange={(value) =>
              setAssignmentFilter(value as 'all' | 'assigned-to-me')
            }
            value={assignmentFilter}
          />

          <ToggleButton
            items={[
              { iconName: 'Kanban', value: 'kanban' },
              { iconName: 'List', value: 'list' }
            ]}
            onChange={(_, value) => setView(value)}
            value={view}
          />
        </Box>

        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          {/*-- real estates --*/}
          <StandardFilter
            disabled={realEstateOptions.length === 0}
            loading={false}
            onChange={(value: string[]) => setSelectedRealEstates(value)}
            options={realEstateOptions}
            selected={selectedRealEstates}
            size="medium"
            title={t('org_units', 'units')}
          />

          {/*-- users --*/}
          <StandardFilter
            loading={false}
            onChange={(value: string[]) => setSelectedUsers(value)}
            options={userOptions}
            selected={activeUsers}
            size="medium"
            title={t('global', 'owner')}
          />

          {/*-- groups --*/}
          <StandardFilter
            disabled={!!locked?.groups}
            loading={false}
            onChange={(value: string[]) => setSelectedGroups(value)}
            options={groupOptions}
            selected={activeGroups}
            size="medium"
            title={t('groups', 'group')}
          />

          <Divider flexItem={true} orientation="vertical" />

          <StandardSearch
            debounce={true}
            search={search}
            setSearch={(value) => setQuery({ search: value })}
          />
        </Box>
      </Box>

      <Text color="text.disabled" sx={{ mb: 1 }} variant="caption">
        {t('global', 'shows')} <b>{filteredItems.length}</b>{' '}
        {t('global', 'of', true)} {tasks.length} {t('tasks', 'tasks', true)}
      </Text>

      {view === 'list' ? (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 2,
            minHeight: 0,
            overflowY: 'auto'
          }}
        >
          {Object.values(ETaskStatus).map((status) => {
            const items = filteredItems.filter((item) => item.status === status)
            const count = items.length
            const defaultCollapsed =
              ((status === ETaskStatus.TODO ||
                status === ETaskStatus.IN_PROGRESS) &&
                count === 0) ||
              status === ETaskStatus.DONE

            return (
              <ContentBox
                color="default"
                collapse={{
                  active: true,
                  defaultCollapsed: defaultCollapsed
                }}
                disablePadding={true}
                header={{
                  icon: {
                    color: translateEnum('task_status', status).color,
                    name: translateEnum('task_status', status).iconName
                  },
                  sx: { px: 2 },
                  title: translateEnum('task_status', status).title,
                  titleAdornment:
                    count !== undefined ? (
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '12px',
                          px: 1,
                          py: 0.25
                        }}
                      >
                        <Text
                          color="text.secondary"
                          sx={{ fontWeight: 700 }}
                          variant="caption"
                        >
                          {count}
                        </Text>
                      </Box>
                    ) : null
                }}
                key={status}
              >
                <Box sx={{ bgcolor: 'background.paper', pt: 0.5 }}>
                  <TasksTable items={items} preview={true} />
                </Box>
              </ContentBox>
            )
          })}
        </Box>
      ) : (
        <KanbanBoard
          columns={TASKS_KANBAN_COLUMNS}
          disableItemPadding={true}
          items={filteredItems.map((task) => {
            return {
              columnId: task.status,
              href: `/tasks/${task.id}`,
              id: task.id
            }
          })}
          onItemMove={handleTaskMove}
          renderItem={(id) => (
            <TaskItem disableBorder={true} id={id} search={search} />
          )}
          sx={{ height: '100%', ...sx }}
        />
      )}
    </>
  )
}

export default TasksKanbanBoard
